import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import AICopilot from "./AICopilot";
import OfflineBanner from "./OfflineBanner";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  House, Lightning, GraduationCap, IdentificationCard, CreditCard, Buildings,
  Books, CalendarBlank, MapTrifold, UsersThree, Exam, Eye, Briefcase, Network, FileText,
  Sparkle, SignOut, CaretDown,
} from "@phosphor-icons/react";

const buildGroups = (t) => [
  {
    label: t("groups.overview"),
    items: [{ to: "/app", icon: House, label: t("nav.dashboard"), end: true, testid: "dashboard" }],
  },
  {
    label: t("groups.pre_enrollment"),
    items: [
      { to: "/app/leads", icon: Lightning, label: t("nav.leads"), testid: "leads", roles: ["admin"] },
      { to: "/app/admissions", icon: GraduationCap, label: t("nav.admissions"), testid: "admissions", roles: ["admin"] },
    ],
  },
  {
    label: t("groups.identity_finance"),
    items: [
      { to: "/app/identity", icon: IdentificationCard, label: t("nav.identity"), testid: "identity", roles: ["admin", "student"] },
      { to: "/app/fees", icon: CreditCard, label: t("nav.fees"), testid: "fees", roles: ["admin", "student", "parent"] },
    ],
  },
  {
    label: t("groups.academic_spine"),
    items: [
      { to: "/app/sis", icon: Buildings, label: t("nav.sis"), testid: "sis", roles: ["admin", "faculty"] },
      { to: "/app/lms", icon: Books, label: t("nav.lms"), testid: "lms", roles: ["admin", "faculty", "student"] },
      { to: "/app/attendance", icon: CalendarBlank, label: t("nav.attendance"), testid: "attendance", roles: ["admin", "faculty", "student", "parent"] },
      { to: "/app/campus", icon: MapTrifold, label: t("nav.campus"), testid: "campus", roles: ["admin", "student", "parent"] },
    ],
  },
  {
    label: t("groups.assessment"),
    items: [
      { to: "/app/parent", icon: UsersThree, label: t("nav.parent"), testid: "parent", roles: ["admin", "parent"] },
      { to: "/app/exams", icon: Exam, label: t("nav.exams"), testid: "exams", roles: ["admin", "faculty", "student", "parent"] },
      { to: "/app/proctoring", icon: Eye, label: t("nav.proctoring"), testid: "proctoring", roles: ["admin", "faculty"] },
    ],
  },
  {
    label: t("groups.outcomes"),
    items: [
      { to: "/app/placements", icon: Briefcase, label: t("nav.placements"), testid: "placements", roles: ["admin", "student"] },
      { to: "/app/alumni", icon: Network, label: t("nav.alumni"), testid: "alumni", roles: ["admin"] },
      { to: "/app/reports", icon: FileText, label: t("nav.reports"), testid: "reports", roles: ["admin"] },
    ],
  },
];

const filterGroupsByRole = (groups, role) => {
  if (!role || role === "admin") return groups.map(g => ({ ...g, items: g.items.filter(i => !i.roles || i.roles.includes(role || "admin")) })).filter(g => g.items.length);
  return groups
    .map(g => ({ ...g, items: g.items.filter(i => !i.roles || i.roles.includes(role)) }))
    .filter(g => g.items.length > 0);
};

export default function AppShell() {
  const { t } = useTranslation();
  const { user, logout, setInstitution, setRole } = useAuth();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [roleMenu, setRoleMenu] = useState(false);
  const nav = useNavigate();
  const inst = user?.institution_type || "higher_ed";
  const groups = filterGroupsByRole(buildGroups(t), user?.role);

  const toggleInst = async () => {
    await setInstitution(inst === "higher_ed" ? "k12" : "higher_ed");
  };

  const switchRole = async (r) => {
    setRoleMenu(false);
    await setRole(r, inst);
    nav("/app");
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-[260px] shrink-0 border-r border-border bg-white flex flex-col" data-testid="app-sidebar">
        <div className="h-14 flex items-center px-5 border-b border-border">
          <Logo />
        </div>
        <div className="px-3 py-3 border-b border-border space-y-2">
          <button
            onClick={toggleInst}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-border hover:bg-secondary text-left"
            data-testid="institution-toggle-btn"
          >
            <div>
              <div className="eos-overline">{t("institution.label")}</div>
              <div className="text-[13px] font-medium tracking-tight mt-0.5">{inst === "k12" ? t("institution.k12") : t("institution.higher_ed")}</div>
            </div>
            <CaretDown size={14} weight="bold" className="text-muted-foreground"/>
          </button>
          <LanguageSwitcher />
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {groups.map((g) => (
            <div key={g.label} className="px-3 mt-3">
              <div className="eos-overline px-2 mb-1.5">{g.label}</div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2 py-1.5 rounded text-[13px] transition-colors ${
                        isActive
                          ? "bg-[hsl(244_53%_20%)] text-white"
                          : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                      }`
                    }
                    data-testid={`nav-${it.testid}`}
                  >
                    {({ isActive }) => (
                      <>
                        <it.icon size={16} weight={isActive ? "fill" : "regular"} className={isActive ? "text-[hsl(20_90%_55%)]" : ""}/>
                        <span className="truncate">{it.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-3 relative">
          {roleMenu && (
            <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-border rounded-md shadow-lg overflow-hidden z-10" data-testid="role-menu">
              <div className="eos-overline px-3 py-2 border-b border-border">{t("app.view_as")}</div>
              {[
                { v: "admin", l: t("roles.admin") },
                { v: "faculty", l: t("roles.faculty") },
                { v: "student", l: t("roles.student") },
                { v: "parent", l: t("roles.parent") },
              ].map(r => (
                <button
                  key={r.v}
                  onClick={() => switchRole(r.v)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary ${user?.role === r.v ? "font-semibold text-[hsl(244_53%_20%)]" : ""}`}
                  data-testid={`switch-role-${r.v}`}
                >
                  {r.l}{user?.role === r.v && " ·"}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setRoleMenu(!roleMenu)}
              className="flex items-center gap-2 min-w-0 flex-1 hover:bg-secondary px-1 py-1 -mx-1 rounded transition-colors"
              data-testid="user-menu-btn"
            >
              <div className="w-8 h-8 rounded-full bg-[hsl(244_53%_20%)] text-white flex items-center justify-center text-[12px] font-semibold shrink-0">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 text-left">
                <div className="text-[12px] font-medium truncate">{user?.name}</div>
                <div className="text-[10px] text-muted-foreground capitalize">{user?.role} · {t("app.switch_role")}</div>
              </div>
            </button>
            <button onClick={logout} className="text-muted-foreground hover:text-foreground p-1" title={t("app.sign_out")} data-testid="logout-btn">
              <SignOut size={16}/>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <OfflineBanner />
        <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between">
          <div className="text-xs text-muted-foreground font-mono-eos">eduos / {window.location.pathname}</div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 text-[11px] text-muted-foreground px-3 py-1.5 border border-border rounded-md">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
              {t("app.all_modules_live")}
            </div>
            <button
              onClick={() => setCopilotOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)] text-white text-sm"
              data-testid="open-copilot-btn"
            >
              <Sparkle size={14} weight="fill" className="text-[hsl(20_90%_55%)]"/>
              {t("app.ask_copilot")}
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      <AICopilot open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
}
